<template>
  <v-main class="profile-bg">
    <v-container fluid class="pa-6">
      <Banner :text="$t('profile.helper')" />

      <!-- Hero Header -->
      <v-card class="profile-hero-card mb-6" elevation="0">
        <div class="profile-hero-header">
          <div class="d-flex align-center" style="gap: 18px;">
            <div class="profile-hero-icon">
              <v-icon color="white" size="28">mdi-account-cog</v-icon>
            </div>
            <div>
              <h1 class="profile-hero-title">{{ $t('profile.settings') }}</h1>
              <p class="profile-hero-subtitle">
                Manage your account preferences and personal information
              </p>
            </div>
          </div>
        </div>
      </v-card>

      <v-row>
        <!-- Profile Info Card -->
        <v-col cols="12" md="6">
          <v-card class="profile-card" elevation="0">
            <div class="profile-card-header profile-card-header--blue">
              <div class="profile-card-header-icon">
                <v-icon color="white" size="20">mdi-account-circle-outline</v-icon>
              </div>
              <h3 class="profile-card-header-title">{{ $t('profile.title') }}</h3>
            </div>
            <div class="profile-card-body">
              <!-- Avatar -->
              <div class="profile-field">
                <div class="profile-field-label">{{ $t('avatar.title') }}</div>
                <div class="d-flex align-center" style="gap: 14px;">
                  <v-avatar size="56px" class="profile-avatar-ring">
                    <img
                      alt="Avatar"
                      :src="require('@/assets/avatars/Frame-' + $store.getters.avatar + '.svg')"
                    />
                  </v-avatar>
                  <v-btn
                    color="primary"
                    :to="{ name: 'Avatar', query: { redirect: 'Profile' } }"
                    small
                    depressed
                    class="profile-change-btn"
                  >
                    <v-icon left small>mdi-pencil-outline</v-icon>
                    {{ $t('avatar.change') }}
                  </v-btn>
                </div>
              </div>
              <!-- Full Name -->
              <div class="profile-field">
                <div class="profile-field-label">{{ $t('fullname') }}</div>
                <div class="profile-field-value">
                  <v-icon small color="#1976d2" class="mr-2">mdi-account-outline</v-icon>
                  {{ $store.getters.fullName }}
                </div>
              </div>
              <!-- Email -->
              <div class="profile-field profile-field--last">
                <div class="profile-field-label">Email</div>
                <div class="profile-field-value">
                  <v-icon small color="#7b1fa2" class="mr-2">mdi-email-outline</v-icon>
                  {{ user.email }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- Timezone & Region Card -->
        <v-col cols="12" md="6">
          <v-card class="profile-card" elevation="0">
            <div class="profile-card-header profile-card-header--purple">
              <div class="profile-card-header-icon profile-card-header-icon--purple">
                <v-icon color="white" size="20">mdi-earth</v-icon>
              </div>
              <h3 class="profile-card-header-title">{{ $t('timezone-region') }}</h3>
            </div>
            <div class="profile-card-body">
              <div class="profile-field">
                <v-autocomplete
                  :value="this.$store.getters.timezone"
                  @change="updateTimezone($event)"
                  :items="getTimezones()"
                  :label="$t('Time Zone')"
                  outlined
                  dense
                  hide-details="auto"
                  class="profile-select"
                  prepend-inner-icon="mdi-clock-outline"
                ></v-autocomplete>
              </div>
              <div class="profile-field profile-field--last">
                <v-autocomplete
                  :value="this.$store.getters.dateFormat"
                  @change="updateDateFormat($event)"
                  :items="getDateFormats()"
                  :label="$t('Date Format')"
                  outlined
                  dense
                  hide-details="auto"
                  class="profile-select"
                  prepend-inner-icon="mdi-calendar-outline"
                ></v-autocomplete>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';
import Banner from '@/components/helpers/Banner';

export default {
  name: 'Profile',
  mixins: [util],
  components: {
    // Header,
    Banner,
  },
  data() {
    return {
      user: {
        email: null,
        timezone: null,
        dateFormat: null,
      },
    };
  },
  mounted() {
    this.axios.get('/api/v1/profile').then(res => (this.user = res.data));
  },
  methods: {
    updateTimezone(event) {
      this.loading = true;
      this.user.timezone = event;
      this.axios
        .post('/api/v1/profile/timezone', this.user)
        .then(() => {
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('timezone-updated'),
          });
          this.$store.commit('setTimezone', this.user.timezone);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateDateFormat(event) {
      this.loading = true;
      this.user.dateFormat = event;
      this.axios
        .post('/api/v1/profile/date-format', this.user)
        .then(() => {
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('date-format-updated'),
          });
          this.$store.commit('setDateFormat', this.user.dateFormat);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
/* Background */
.profile-bg {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  min-height: 100vh;
}

/* Hero Card */
.profile-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
}

.profile-hero-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 24px 28px;
}

.profile-hero-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.profile-hero-title {
  color: white;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
}

.profile-hero-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin: 2px 0 0;
}

/* Profile Cards */
.profile-card {
  border-radius: 16px !important;
  border: 1px solid #e5e7eb !important;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
}
.profile-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08) !important;
  transform: translateY(-2px);
}

.profile-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 22px;
}
.profile-card-header--blue {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
}
.profile-card-header--purple {
  background: linear-gradient(135deg, #7b1fa2, #ab47bc);
}

.profile-card-header-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.profile-card-header-title {
  color: white;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: 0.3px;
}

/* Card Body */
.profile-card-body {
  padding: 20px 22px;
}

.profile-field {
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid #f0f0f0;
}
.profile-field--last {
  border-bottom: none;
  padding-bottom: 0;
  margin-bottom: 0;
}

.profile-field-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #90a4ae;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 8px;
}

.profile-field-value {
  font-size: 0.95rem;
  font-weight: 500;
  color: #37474f;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: linear-gradient(135deg, #f5f7fa 0%, #fafbfc 100%);
  border-radius: 10px;
  border: 1px solid #e8ecf1;
}

/* Avatar Ring */
.profile-avatar-ring {
  border: 3px solid transparent;
  background-image: linear-gradient(white, white),
    linear-gradient(135deg, #1976d2, #42a5f5, #7b1fa2);
  background-origin: border-box;
  background-clip: content-box, border-box;
}

/* Change Button */
.profile-change-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
  letter-spacing: 0 !important;
}

/* Select Inputs */
.profile-select {
  border-radius: 10px;
}
.profile-select ::v-deep .v-input__slot {
  border-radius: 10px !important;
}
</style>
